import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import {
  Estimate,
  Quote,
  VendorOrder,
} from '../../../../../../shared/models/vendor-order.model';
import { toIsoCurrency } from '../../../../../../shared/utils/xtm-currency.utils';
import { QuoteCostCardComponent } from '../../quotes/quote-cost-card/quote-cost-card.component';

export interface ApproveFinalCostResult {
  purchaseOrderId: string;
}

export type ApproveFinalCostDialogResult =
  | 'back'
  | ApproveFinalCostResult
  | undefined;

@Component({
  selector: 'app-approve-final-cost-modal',
  imports: [MatIcon, QuoteCostCardComponent],
  templateUrl: './approve-final-cost-modal.component.html',
  styleUrl: './approve-final-cost-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApproveFinalCostModalComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<unknown, ApproveFinalCostDialogResult>,
  );

  readonly order = input.required<VendorOrder>();
  readonly estimate = input.required<Estimate>();
  readonly reanalysis = input.required<Quote>();
  readonly purchaseOrderId = signal('');

  readonly deliveryType = computed(() => {
    const estimate = this.estimate();
    if (estimate.rushedDateSelected === true) return 'Rushed';
    return estimate.quote?.rushed && !estimate.quote?.normal
      ? 'Rushed'
      : 'Standard';
  });

  readonly deliveryDate = computed(() => {
    const estimate = this.estimate();
    const raw =
      this.deliveryType() === 'Rushed'
        ? estimate.estimatedDeliveryDates?.rushed?.estimatedDeliveryDate
        : estimate.estimatedDeliveryDates?.normal?.estimatedDeliveryDate;
    return this.formatDate(raw ?? estimate.deadline);
  });

  readonly quoteId = computed(() => this.order().jobNumber ?? '--');
  readonly currency = computed(() =>
    toIsoCurrency(this.reanalysis().currency ?? this.estimate().currency),
  );
  readonly totalCost = computed(
    () =>
      this.reanalysis().totalCost ??
      (this.reanalysis().costs ?? []).reduce((sum, item) => sum + item.cost, 0),
  );

  ngOnInit(): void {
    const purchaseOrderId = this.order().purchaseOrderId;
    this.purchaseOrderId.set(
      purchaseOrderId == null ? '' : String(purchaseOrderId),
    );
  }

  onPurchaseOrderInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const sanitizedValue = inputElement.value
      .replaceAll(/\D/g, '')
      .slice(0, 20);
    inputElement.value = sanitizedValue;
    this.purchaseOrderId.set(sanitizedValue);
  }

  onBack(): void {
    this.dialogRef.close('back');
  }

  getValue(): ApproveFinalCostResult {
    return { purchaseOrderId: this.purchaseOrderId() };
  }

  private formatDate(raw: string | undefined): string {
    if (!raw) return '--';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '--';
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }
}
